import React from "reactn";
import { formatter } from "../../../../core/utilities/formatter";
import { ShowDialog } from "../../../components/Dialogs/BaseDialog";
import UpdateEmailRecipientDialog from "../dialogs/UpdateEmailRecipientDialog";
import { Icon } from "@material-ui/core";
import PromoField from "js/react/views/UserOptions/components/PromoField";
import Api from "js/core/api";

function NextBillMessage({ subscription, nextInterval }) {
    const nextBillDate = formatter.formatTimestamp(subscription.current_period_end, true);

    if (subscription.status == "trialing") {
        if (subscription.cancel_at_period_end && subscription.product.includes("team")) {
            return (
                <span>
                    Your trial has been canceled.
                    <br />
                    <br />

                    On {nextBillDate}, your team will be deleted. All members will lose access to Team Plan features, presentations, and data in this workspace.
                </span>
            );
        } else if (subscription.cancel_at_period_end) {
            return (
                <span>
                    Your subscription has been canceled. You will be downgraded to the Basic (free) plan when your
                    trial ends on {nextBillDate}.
                </span>
            );
        } else {
            return (
                <span>
                    Your first {nextInterval}ly bill of <strong>${(subscription.upcomingInvoice.amount_due / 100).toFixed(2)}</strong> will be due when your free trial ends
                    on <strong>{nextBillDate}</strong>.
                </span>
            );
        }
    } else {
        if (subscription.cancel_at_period_end && subscription.product.includes("team")) {
            return (
                <span>
                    Your plan has been canceled.
                    <br />
                    <br />

                    On {nextBillDate}, your team will be deleted. All members will lose access to Team Plan features, presentations, and data in this workspace.
                </span>
            );
        } else if (subscription.cancel_at_period_end) {
            return (
                <span>
                    Your subscription has been canceled. You will be downgraded to the Basic (free) plan at the end
                    of your current billing period on <strong>{nextBillDate}</strong>.
                </span>
            );
        } else {
            return (
                <span>
                    Your next {nextInterval}ly bill of <strong>${(subscription.upcomingInvoice.amount_due / 100).toFixed(2)}</strong> is due on{" "}
                    <strong>{nextBillDate}</strong>.
                </span>
            );
        }
    }
}

function Discount({ subscription, onPromoApplied }) {
    if (!subscription.upcomingInvoice) {
        return null;
    }
    return (
        <div style={{ marginTop: ".5em" }}>
            {subscription.upcomingInvoice.discount
                ? <DiscountDesciption subscription={subscription} />
                : <DiscountField subscription={subscription} onPromoApplied={onPromoApplied} />
            }
        </div>
    );
}

function DiscountDesciption({ subscription }) {
    return (
        <span style={{
            fontSize: 15,
            textTransform: "uppercase",
            fontWeight: 900,
        }}><PromoIcon /> {subscription.upcomingInvoice.discount.name}{" "}
            ({subscription.upcomingInvoice.discount.offer})
        </span>
    );
}

function PromoIcon() {
    return (
        <Icon style={{ color: "#8FB130", fontSize: 22.5, verticalAlign: -6, paddingRight: 3 }}>confirmation_number</Icon>
    );
}

class DiscountField extends React.Component {
    state = {
        isEditing: false
    };

    handleFetchPromo = async code => {
        // basic validation to report nice errors
        const promo = await Api.promos.get({ code });
        if (promo.error) {
            return promo;
        }

        // Stripe is willing to accept coupons which fail the appliesTo condition,
        // it just doesn't result in a discount at invoice-time. That's not ideal, so catch it.
        const plan = this.props.subscription.product.includes("team") ? "team" : "pro";
        if (promo.appliesTo && !promo.appliesTo.includes(plan)) {
            return { error: `This coupon can't be applied to a ${plan} plan` };
        }

        // stripe performs full restriction validation
        const apply = await Api.promos.post({
            promoCodeId: promo.id,
            subscription: this.props.subscription.id
        });

        if (apply.error) {
            return apply;
        }

        // give the user a chance to see it worked
        setTimeout(() => {
            this.props.onPromoApplied(promo);
        }, 2000);

        return promo;
    };

    render() {
        if (!this.state.isEditing) {
            return (
                <span
                    style={{
                        cursor: "pointer",
                        color: "#159BCD",
                        fontSize: 14,
                        textTransform: "uppercase",
                        fontWeight: 900,
                    }}
                    onClick={() => this.setState({ isEditing: true })}
                >
                    <PromoIcon />
                    Add Promo Code
                </span>
            );
        } else {
            return (
                <div style={{ maxWidth: 400 }}>
                    <PromoField
                        fetchPromo={this.handleFetchPromo}
                        fetchKey={this.props.subscription.planId}
                        onClear={() => {}}
                        autoFocus
                    />
                </div>
            );
        }
    }
}

export const NextPaymentSection = function({ subscription, nextInterval, availableCredit, onUpdateEmail, onPromoApplied }) {
    const isTeam = subscription.product.includes("team");

    // infer credit usage
    let invoiceCreditUsed = 0;
    if (subscription.upcomingInvoice) {
        const invoice = subscription.upcomingInvoice;
        if (invoice.amount_due < invoice.total) {
            // invoice is paid partly from balance
            invoiceCreditUsed = invoice.total - invoice.amount_due;
        } else if (invoice.total < 0) {
            // invoice itself contains net credit
            invoiceCreditUsed = availableCredit - (-invoice.total);
        } else {
            // invoice contains credit but not net
            invoiceCreditUsed += subscription.upcomingInvoice.referralCredit;
        }
    }

    const price = subscription.items.data[0].price;
    const isRegularPricing = price.nickname.startsWith("Team-") || price.nickname.startsWith("Pro-");

    return (
        <div className="billing-block">
            <div className="title">Next Bill</div>
            <div className="contents">
                <div style={{ fontSize: 20, paddingBottom: 40, marginBottom: 10 }}>
                    <NextBillMessage subscription={subscription} nextInterval={nextInterval} />
                    {isRegularPricing && (
                        <Discount subscription={subscription} onPromoApplied={onPromoApplied} />
                    )}
                    {!isTeam && !subscription.cancel_at_period_end && availableCredit > 0 && (
                        <div style={{ fontSize: "90%", paddingTop: 10 }}>
                            <Icon style={{ color: "#fa0", fontSize: "1.25em", verticalAlign: -5, paddingRight: 3 }}>monetization_on</Icon>{" "}
                            {invoiceCreditUsed == availableCredit
                                ? <>Your <b>${(invoiceCreditUsed / 100).toFixed(2)}</b> credit has been applied to this bill.</>
                                : <><b>${(invoiceCreditUsed / 100).toFixed(2)}</b> of your <b>${(availableCredit / 100).toFixed(2)}</b> credit has been applied to this bill.</>
                            }
                        </div>
                    )}
                    {subscription.status === "past_due" && (
                        <div className="past-due">Past Due</div>
                    )}
                </div>
                <div style={{ fontSize: 15, marginTop: 35 }}>
                    Receipts will be sent to: <strong>{subscription.customerEmail}</strong>
                </div>
                <div
                    onClick={() => ShowDialog(UpdateEmailRecipientDialog, { customerEmail: subscription.customerEmail, onAccept: onUpdateEmail })}
                    style={{
                        color: "#159BCD",
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontWeight: 900,
                        fontSize: 14,
                        marginTop: 10
                    }}
                >
                    Update
                </div>
            </div>
        </div>
    );
};
